import {
  createStyles,
  Button,
  FileInput,
  Modal,
  Group,
  Anchor,
  Text,
  Menu,
  Select,
  Tooltip,
} from "@mantine/core";
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  IconEdit,
  IconExclamationCircle,
  IconCirclePlus,
  IconDownload,
  IconChevronDown,
} from "@tabler/icons";
import toast from "react-hot-toast";
import _ from "lodash";

import { useServerApi } from "../hooks/userServerApi";
import { useFormRender } from "../hooks/useFormRender";
import PageListHeader from "./pageListHeader";
import DataTable from "./dataTable";

import { Page } from "../layout/page";
import ReactJson from "react-json-view";
import { useLocalStorage, useDocumentTitle } from "@mantine/hooks";
import { useLocation } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { resolveLinkedPageAccess } from "../helpers/linkedPageAccess";
import { useAuthUser } from "react-auth-kit";
import { useAccessRight } from "../hooks/useAccessRight";
import { useActionRight } from "../hooks/useActionRight";
import { buildSearchQuery } from "../helpers/searchQuery";
import {
  formatImportSummary,
  getImportErrorMessage,
  hasImportWarnings,
} from "../helpers/importResultUtils";
import { authName } from "../App";
import { subscribeListRefresh } from "../context/tabContext";
import { refreshPageListAfterMutation } from "../helpers/pageListRefresh";
import {
  createFetchRequestTracker,
  executeTrackedPageListFetch,
} from "../helpers/pageListFetchRequest";
import { NOTIFICATION_INBOX_REFRESH } from "../hooks/useNotificationInbox";
import { useTabNavigate } from "../hooks/useTabNavigate";
import {
  buildColumnFilterClause,
  buildDeselectAllClause,
  expandTableFilterQuery as expandColumnFilterQuery,
} from "../helpers/tableFilterQuery";

export { buildSearchQuery };

export const ENUM_FORM_DISPLAY = {
  DRAWER: 0,
  MODAL: 1,
  FULLSCREEN: 2,
  INSIDE: 3,
  PAGE: 4,
  LINK: 5,
  NEWTAB: 6,
};

export const ENUM_FORM_LAYOUT_CONTAINER = {
  GRID: 0,
  TABS: 1,
  SECTION: 2,
  WIZARD: 3,
  BOX: 4,
  EMPTY: 5,
};

const getTableColumns = (tableSchema) =>
  typeof tableSchema?.columns === "function"
    ? tableSchema.columns()
    : tableSchema?.columns ?? [];

const getPrimaryRowAction = (tableSchema) => {
  const actionCol = getTableColumns(tableSchema).find((c) => c.isAction);
  return actionCol?.actions?.[0]?.name ?? null;
};

const expandTableFilterQuery = (tableFilterQuery, tableSchema) =>
  expandColumnFilterQuery(tableFilterQuery, tableSchema, getTableColumns);

export function PageList({
  apiEntity,
  title,
  setDocumentTitle = false,
  formSchema,
  tableSchema,
  showAddButton = true,

  hideActionButton = false,
  hideSearchBar = false,
  stats = null,
  filter = null,
  filterQuery = null,
  customActions = null,
  header = null,
  showImport = false,
  reloadAfterSave = false,
  preQueryNotEmpty = false,
  preQuery = null,
  onSubmit,
  onChange,
  searchByTextField = false,
  defaultPageSize = 50,
  readOnly = false,
  /** When route has no nav menu entry, inherit ACL from this entity link (e.g. "order"). */
  accessLinkEntity = null,

  initSort = { by: "createdAt", order: "desc" },
  tablePadding = "xl",
  tableHeader,
  importData,
  onImportSuccess,
  onDeleteSuccess,
  onFetchSuccess,
  showTemplateDownload = false,
  templateDownloads,
  templateDownloadUrl,
  templateDownloadText = "Download Template",
  importVariants,
  /** Entity key for import action permission (defaults to apiEntity). */
  importActionEntity = null,
  columnFilterLayout,
  emptyState = null,
}) {
  const tableRef = useRef();
  const statsRef = useRef();
  const fetchRequestTrackerRef = useRef(null);
  if (!fetchRequestTrackerRef.current) {
    fetchRequestTrackerRef.current = createFetchRequestTracker();
  }

  const [renderForm, formAction, formStatus, formSetting] = useFormRender(
    formSchema,
    () => {
      refreshPageListAfterMutation({ fetchData, statsRef });
      // Handle reloadAfterSave for LINK display mode
      if (reloadAfterSave) {
        const editSchema = formSchema?.edit ?? formSchema;
        if (editSchema?.display?.mode === ENUM_FORM_DISPLAY.LINK) {
          // Reload form data instead of entire window
          if (formAction && formAction.reload) {
            formAction.reload();
          }
        }
      }
    },
    onSubmit,
    onChange
  );

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    totalPage: 1,
    page: 1,
    total: 0,
  });

  const location = useLocation();
  const query = new URLSearchParams(useLocation().search);
  const linkId = query.get("id");
  const linkMode = query.get("mode");
  const linkPath = useLocation().pathname;
  const { t } = useTranslation();

  const [getAccessRight, accessRight, navLinkId] = useAccessRight();
  const { isActionEnabled } = useActionRight();
  const auth = useAuthUser();
  const { userRole } = auth();

  const resolveLinkedAccessRight = () =>
    resolveLinkedPageAccess({
      linkPath,
      accessRight,
      userRole,
      accessLinkEntity,
      getAccessRight,
    });

  const effectiveAccessRight = resolveLinkedAccessRight();

  const [tQueryStorage, settQueryStorage] = useLocalStorage({
    key: "tableQuery" + linkPath + apiEntity + (tableSchema?.name ?? ""),
    defaultValue: {},
  });

  const [pageSize, setPageSize] = useLocalStorage({
    key: "mbid-page-size",
    defaultValue: defaultPageSize,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState({});
  const [tableFilterQuery, setTableFilterQuery] = useState(tQueryStorage);
  const [advancedFilterQuery, setAdvancedFilterQuery] = useState({});
  const [openedImport, setOpenedImport] = useState(false);
  const [activeImportVariant, setActiveImportVariant] = useState(null);
  const [importMode, setImportMode] = useState("insert");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [sort, setSort] = useState(initSort);
  const [api] = useServerApi();
  const tabNav = useTabNavigate();

  // Add ResizeObserver error handling
  useEffect(() => {
    const resizeObserverErrorHandler = (error) => {
      if (error.message.includes("ResizeObserver loop")) {
        // Ignore ResizeObserver loop errors
        console.warn("ResizeObserver loop error detected and ignored");
      } else {
        // Log other errors as usual
        console.error(error);
      }
    };

    window.addEventListener("error", resizeObserverErrorHandler);

    return () => {
      window.removeEventListener("error", resizeObserverErrorHandler);
    };
  }, []);

  const buildQuery = () => {
    const searchTextQuery = searchText
      ? {
          searchText: {
            $regex: searchText,
            $options: "i",
          },
        }
      : {};

    // console.log("buildQuery", {
    //   preQuery: tableSchema.preQuery || preQuery,
    //   searchQuery: searchByTextField ? searchTextQuery : searchQuery,
    //   filterQuery: filterQuery,
    //   tableFilterQuery: tableFilterQuery,
    //   advancedFilterQuery: advancedFilterQuery,
    // });

    const fieldSearchQuery =
      !searchByTextField && searchText
        ? buildSearchQuery(searchText, tableSchema.searchableFields)
        : {};

    const queries = [
      tableSchema.preQuery || preQuery,
      searchByTextField ? searchTextQuery : fieldSearchQuery,
      filterQuery,
      expandTableFilterQuery(tableFilterQuery, tableSchema),
      advancedFilterQuery,
    ].filter((f) => !_.isEmpty(f));

    if (_.isEmpty(queries)) return {};
    return { $and: queries };
  };

  //Fetch Remote Data function
  const fetchData = useCallback(async () => {
    await executeTrackedPageListFetch({
      tracker: fetchRequestTrackerRef.current,
      fetchFn: async () => {
        if (
          preQueryNotEmpty &&
          _.isEmpty(tableSchema.preQuery) &&
          _.isEmpty(preQuery)
        ) {
          return undefined;
        }

        const q = buildQuery();
        return api.search({
          apiEntity,
          pageSize,
          currentPage,
          sort: sort.by ? sort : initSort,
          select: tableSchema.select,
          searchQuery: q,
          searchText,
          searchByTextField,
          preQuery: { ...tableSchema.preQuery, ...preQuery },
          byAggregation: tableSchema.byAggregation,
        });
      },
      onEmpty: () => {
        setRows([]);
        setPagination({
          totalPage: 0,
          page: 1,
          total: 0,
        });
      },
      onSuccess: (data) => {
        setPagination({
          ...pagination,
          total: data.total,
          totalPage: Math.ceil(data.total / data.limit),
        });
        setRows(data.docs ?? []);
        onFetchSuccess?.(data.total ?? 0, data.docs ?? []);
      },
      onError: () => {
        setRows([]);
        onFetchSuccess?.(0, []);
        toast.error(`Fetch Error`);
      },
    });
  });

  useEffect(() => {
    if (linkId || linkMode === "add") return;
    return subscribeListRefresh(linkPath, () => {
      refreshPageListAfterMutation({ fetchData, statsRef });
    });
  }, [linkPath, linkId, linkMode, fetchData]);

  useEffect(() => {
    if (apiEntity !== "userNotification") return;
    const onInboxRefresh = () => {
      refreshPageListAfterMutation({ fetchData, statsRef });
    };
    window.addEventListener(NOTIFICATION_INBOX_REFRESH, onInboxRefresh);
    return () => window.removeEventListener(NOTIFICATION_INBOX_REFRESH, onInboxRefresh);
  }, [apiEntity, fetchData]);

  const onPageChange = useCallback((page) => {
    setCurrentPage(page);
    setPagination({ ...pagination, page });
  });

  const onPageSizeChange = useCallback((pageSize) => {
    // console.log("onPageSizeChange", pageSize);
    setCurrentPage(1);
    setPageSize(pageSize);
    setPagination({ ...pagination, page: 1 });
  });

  const onSortChange = useCallback((by, reverseSortDirection) => {
    if (!by) return;
    setSort({ by, order: reverseSortDirection ? "desc" : "asc" });
  });

  const onSearchChange = useCallback((searchText) => {
    let text = searchText;
    if (currentPage > 1) {
      setCurrentPage(1);
      setPagination({ ...pagination, page: 1 });
    }
    setSearchText(text);
  });

  const onTableFilterChange = useCallback((field, values, possibleValues) => {
    // console.log("onTableFilterChange", field, values, possibleValues);
    if (!field) return;
    const newFilterQuery = { ...tableFilterQuery };
    const columns = getTableColumns(tableSchema);
    const filterCol = columns.find((c) => c.filter?.field === field);

    // Deselect all — match nothing until the user selects at least one option
    if (values.length === 0) {
      newFilterQuery[field] = buildDeselectAllClause(filterCol);
      setCurrentPage(1);
      setTableFilterQuery(newFilterQuery);
      settQueryStorage(newFilterQuery);
      return;
    }

    //All selected
    if (values && possibleValues && values.length === possibleValues.length) {
      delete newFilterQuery[field];
      setCurrentPage(1);
      setTableFilterQuery(newFilterQuery);
      settQueryStorage(newFilterQuery);

      return;
    }
    //Partial selected
    newFilterQuery[field] = buildColumnFilterClause(filterCol, values);
    setCurrentPage(1);
    setTableFilterQuery(newFilterQuery);
    settQueryStorage(newFilterQuery);
    // console.log("newFilterQuery", newFilterQuery);
  });

  const onAdvancedFilterChange = useCallback((query) => {
    setCurrentPage(1);
    setAdvancedFilterQuery(query);
  });

  useEffect(() => {
    let query =
      searchText === ""
        ? {}
        : buildSearchQuery(searchText, tableSchema.searchableFields);
    setSearchQuery(query);
  }, [searchText]);

  useDeepCompareEffect(() => {
    if (_.isEmpty(tQueryStorage)) return;
    if (_.isEqual(tQueryStorage, tableFilterQuery)) return;
    setTableFilterQuery(tQueryStorage);
  }, [tQueryStorage]);

  useEffect(() => {
    // console.log(sort);
    fetchData();
  }, [
    searchQuery,
    searchText,
    pageSize,
    sort,
    currentPage,
    tableSchema.preQuery,
    preQuery,
    filterQuery,
    tableFilterQuery,
    advancedFilterQuery,
  ]);

  const copyToNew = async (data) => {
    let newData = await api.copyById({
      apiEntity,
      id: data._id,
      newCode: true,
      fields: tableSchema?.copyToNewFields,
      uniqueFields: tableSchema?.uniqueFields,
    });

    return newData;
  };

  const onActionBtnClick = useCallback(async ({ action, data }) => {
    // console.log("onActionBtnClick", action, data, readOnly, accessRight);

    if (tableSchema.onActionBtnClickCustom) {
      tableSchema.onActionBtnClickCustom({
        action,
        data,
        formAction,
        fetchData,
      });
      return;
    }
    switch (action) {
      case "show":
        formAction.open({ mode: "show", id: data._id });
        break;
      case "viewLinkage":
        formAction.open({ mode: "show", id: data._id });
        break;
      case "edit":
        // console.log("location edit", `${location.pathname}?id=${data._id}`);
        // formAction.openIdByLink({ mode: "edit", id: data._id });
        formAction.open({ mode: "edit", id: data._id });
        break;
      case "copy":
        const newRecord = await copyToNew(data);
        formAction.open({ mode: "edit", id: newRecord._id });
        break;
      case "delete":
        // console.log("delete", data._id);
        if (window.confirm("Are you sure to delete this data?")) {
          await api.removeById({ apiEntity, id: data._id });
          refreshPageListAfterMutation({ fetchData, statsRef });
          toast.success("Deleted Successfully");
          if (onDeleteSuccess) onDeleteSuccess();
        }
        break;
      case "unDo":
        console.log("unDo", data);
        break;
    }
  });

  const handleRowDoubleClick = useCallback(
    ({ data }) => {
      if (typeof tableSchema.onRowDoubleClick === "function") {
        tableSchema.onRowDoubleClick({ data, formAction });
        return;
      }
      if (readOnly || tableSchema.disableRowDoubleClick) return;
      const action = getPrimaryRowAction(tableSchema);
      if (!action) return;
      onActionBtnClick({ action, data });
    },
    [readOnly, tableSchema, onActionBtnClick, formAction]
  );

  const getTitle = () => {
    if (
      formSetting.displayMode !== ENUM_FORM_DISPLAY.INSIDE &&
      formSetting.displayMode !== ENUM_FORM_DISPLAY.LINK
    ) {
      return t(title);
    }

    if (!formStatus.isOpen) return t(title);
    //Set window title

    return formAction.getTitle();
  };

  const showTable = () => {
    if (!formStatus.isOpen) return true;

    if (
      formSetting.displayMode !== ENUM_FORM_DISPLAY.INSIDE &&
      formSetting.displayMode !== ENUM_FORM_DISPLAY.LINK
    )
      return true;

    return false;
  };

  const showEmptyState =
    emptyState &&
    rows.length === 0 &&
    pagination.total === 0 &&
    (!emptyState.when || emptyState.when({ rows, pagination, preQuery }));

  const resolvedImportVariants =
    importVariants?.length > 0
      ? importVariants
      : showImport
        ? [
            {
              key: "default",
              label: t("Import"),
              templateDownloads: showTemplateDownload
                ? templateDownloads?.length > 0
                  ? templateDownloads
                  : templateDownloadUrl
                    ? [
                        {
                          url: templateDownloadUrl,
                          text: templateDownloadText,
                        },
                      ]
                    : []
                : [],
            },
          ]
        : [];

  const openImportVariant = (variant) => {
    setActiveImportVariant(variant);
    setImportFile(null);
    setImportMode(variant.defaultImportMode ?? variant.importModes?.[0]?.value ?? "insert");
    setOpenedImport(true);
  };

  const getPageListAction = () => {
    const actions = [];

    if (customActions?.length) {
      actions.push(
        ...customActions.filter(
          (action) =>
            !action.requiresWrite || (!readOnly && effectiveAccessRight === 15)
        )
      );
    }

    if (showAddButton && !readOnly && effectiveAccessRight === 15)
      actions.push({
        label: t("Add New"),
        onClick: () => formAction.open({ mode: "add" }),
      });

    const importEntity = importActionEntity ?? apiEntity;
    const canImport =
      resolvedImportVariants.length > 0 &&
      !readOnly &&
      effectiveAccessRight === 15 &&
      isActionEnabled(importEntity, "import");

    if (canImport) {
      if (resolvedImportVariants.length === 1) {
        actions.push({
          label: t("Import"),
          onClick: () => openImportVariant(resolvedImportVariants[0]),
          variant: "default",
        });
      } else {
        actions.push({
          label: t("Import"),
          variant: "default",
          render: () => (
            <Menu shadow="md" width={200} withinPortal>
              <Menu.Target>
                <Button size="xs" variant="default" rightIcon={<IconChevronDown size={16} />}>
                  {t("Import")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {resolvedImportVariants.map((variant) => (
                  <Menu.Item
                    key={variant.key}
                    onClick={() => openImportVariant(variant)}
                  >
                    {variant.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ),
        });
      }
    }

    actions.push({
      label: t("Refresh"),
      onClick: async () => {
        //Clear storage first  with "tableQuery" + linkPath
        // console.log(
        //   "Refresh >Clear storage",
        //   "tableQuery" + linkPath + apiEntity + (tableSchema?.name ?? "")
        // );
        localStorage.removeItem(
          "tableQuery" + linkPath + apiEntity + (tableSchema?.name ?? "")
        );
        //reload the browser
        window.location.reload();
        return;

        await fetchData();
        if (stats && stats.component) {
          if (statsRef.current) {
            // console.log("PageList >fetchData >refresh");
            statsRef.current.refresh();
          }
        }
      },
      variant: "default",
    });
    actions.push({
      label: t("Close"),
      onClick: () => tabNav.closeActive(),
      variant: "default",
    });

    // console.log("actions", actions);
    return actions;
  };

  const getPageFormAction = () => {
    const actions = [];
    if (!readOnly && effectiveAccessRight === 15 && !formStatus.isMcaLocked)
      actions.push({ label: t("Save"), onClick: () => formAction.save() });

    if (formStatus.mode === "edit" && formStatus.showMcaSubmit) {
      actions.push({
        label: t("Submit for Approval"),
        onClick: () => formAction.submitForApproval?.(),
        color: "green",
        loading: formStatus.mcaSubmitting,
        disabled:
          !formStatus.canSubmitMcaCreate || !!formStatus.mcaSubmitDisabledReason,
        render:
          formStatus.mcaSubmitDisabledReason
            ? () => (
                <Tooltip
                  label={formStatus.mcaSubmitDisabledReason}
                  withArrow
                  key="mca-submit"
                >
                  <span>
                    <Button
                      size="xs"
                      color="green"
                      loading={formStatus.mcaSubmitting}
                      disabled
                      onClick={() => formAction.submitForApproval?.()}
                    >
                      {t("Submit for Approval")}
                    </Button>
                  </span>
                </Tooltip>
              )
            : undefined,
      });
    }

    actions.push({
      label: t("Close"),
      onClick: () => {
        const closed = formAction.close(true);
        if (!closed) return;
        if (formSetting.displayMode === ENUM_FORM_DISPLAY.INSIDE) {
          fetchData();
        }
      },
      variant: "default",
    });
    return actions;
  };

  const pageActionButtons = () => {
    if (!formStatus.isOpen) return getPageListAction();
    if (
      formSetting.displayMode !== ENUM_FORM_DISPLAY.INSIDE &&
      formSetting.displayMode !== ENUM_FORM_DISPLAY.LINK
    )
      return getPageListAction();

    return getPageFormAction();
  };

  const handleImport = async () => {
    try {
      if (!importFile || !activeImportVariant) return;
      setImporting(true);

      let result;
      if (activeImportVariant.onImport) {
        result = await activeImportVariant.onImport(
          importFile,
          { importMode, importData },
          api
        );
      } else {
        result = await api.importBatch({
          apiEntity: activeImportVariant.apiEntity ?? apiEntity,
          file: importFile,
          importData: {
            ...(importData ?? {}),
            ...(activeImportVariant.importModes?.length
              ? { importMode }
              : {}),
          },
        });
      }

      if (!result.success) {
        throw result.error ?? { message: result.message ?? "Import failed" };
      }

      const importSummary = result.data;
      const importMessage = formatImportSummary(importSummary);
      const hasWarnings = hasImportWarnings(importSummary);

      showNotification({
        title: hasWarnings
          ? `${importFile.name} Import completed with warnings`
          : `${importFile.name} Import successfully`,
        color: hasWarnings ? "yellow" : "green",
        icon: hasWarnings ? (
          <IconExclamationCircle size={18} />
        ) : undefined,
        message: (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {importMessage}
          </Text>
        ),
        autoClose: hasWarnings ? false : undefined,
      });
      setOpenedImport(false);
      setImporting(false);
      refreshPageListAfterMutation({ fetchData, statsRef });
      if (onImportSuccess) onImportSuccess();
    } catch (error) {
      console.log(error);
      showNotification({
        title: `${importFile?.name ?? "File"} Import failed`,
        icon: <IconExclamationCircle size={18} />,
        color: "red",
        message: (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {getImportErrorMessage(error)}
          </Text>
        ),
        autoClose: false,
      });
      setImporting(false);
    }
  };

  const activeTemplateDownloads =
    activeImportVariant?.templateDownloads?.length > 0
      ? activeImportVariant.templateDownloads
      : showTemplateDownload
        ? templateDownloads?.length > 0
          ? templateDownloads
          : templateDownloadUrl
            ? [{ url: templateDownloadUrl, text: templateDownloadText }]
            : []
        : [];

  const downloadTemplate = async (url, text) => {
    try {
      const token = localStorage.getItem(authName);
      const result = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "x-access-token": token,
        },
      });

      const contentType = result.headers.get("Content-Type") ?? "";
      if (!result.ok || contentType.includes("application/json")) {
        const errorBody = contentType.includes("application/json")
          ? await result.json()
          : null;
        throw new Error(
          errorBody?.message ?? errorBody?.error ?? "Failed to download template"
        );
      }

      const blob = await result.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      const contentDisposition = result.headers.get("Content-Disposition");
      let fileName = `${text || "template"}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match?.[1]) fileName = match[1];
      }
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showNotification({
        title: "Download failed",
        color: "red",
        icon: <IconExclamationCircle size={18} />,
        message: error?.message ?? String(error),
      });
    }
  };

  return (
    <Page>
      {/* navLinkId: {navLinkId} */}
      {/* accessRight: {accessRight} */}
      {/* <ReactJson
        src={userRole}
        style={{ background: "white" }}
        collapsed
      ></ReactJson> */}
      {/* linkPath: {linkPath} */}
      {/* <>sort: {JSON.stringify(sort)}</> */}
      {/* <ReactJson
        src={preQuery}
        style={{ background: "white" }}
        collapsed
      ></ReactJson>
      <ReactJson
        src={tableSchema.preQuery}
        style={{ background: "white" }}
        collapsed
      ></ReactJson> */}
      {/* <ReactJson
        src={formAction.getValues()}
        style={{ background: "white" }}
        collapsed
      ></ReactJson> */}
      {/* searchText : {searchText} */}
      {/* <ReactJson
        src={tableFilterQuery}
        style={{ background: "white" }}
        collapsed
      ></ReactJson>
      <ReactJson
        src={rows}
        style={{ background: "white" }}
        collapsed
      ></ReactJson> */}
      {/* <ReactJson
        src={tQueryStorage}
        style={{ background: "white" }}
        collapsed
      ></ReactJson>
      <ReactJson
        src={tableFilterQuery}
        style={{ background: "white" }}
        collapsed
      ></ReactJson> */}
      {/* Page : {currentPage} */}
      {/* apiEntity: {apiEntity} */}
      <div>
        {showTable() && header && <header.component {...header.props} />}
        {hideActionButton ? null : (
          <PageListHeader
            title={getTitle()}
            actionButtons={pageActionButtons()}
          />
        )}
        {showTable() && stats && (
          <stats.component {...stats.props} ref={statsRef} />
        )}
        {showTable() && filter && <filter.component {...filter.props} />}
        {/* <Space h={"xl"}/> */}

        {showEmptyState && (
          <Text color="dimmed" align="center" py={48} size="md">
            {emptyState.message}
          </Text>
        )}

        {showTable() && !showEmptyState && (
          <DataTable
            ref={tableRef}
            data={rows}
            columns={getTableColumns(tableSchema)}
            pagination={pagination}
            hideSearchBar={hideSearchBar}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSortChange={onSortChange}
            onSearchChange={onSearchChange}
            onFilterChange={onTableFilterChange}
            tableFilterQuery={tableFilterQuery}
            onActionBtnClick={onActionBtnClick}
            onRowDoubleClick={handleRowDoubleClick}
            readonly={readOnly}
            searchText={searchText}
            padding={tablePadding}
            tableHeader={tableHeader}
            advanceFilters={tableSchema.advanceFilters}
            onAdvancedFilterChange={onAdvancedFilterChange}
            advancedFilterQuery={advancedFilterQuery}
            columnFilterLayout={
              tableSchema.columnFilterLayout ?? columnFilterLayout ?? "toolbar"
            }
          />
        )}
        {/*Import Modal */}
        <Modal
          opened={openedImport}
          onClose={() => setOpenedImport(false)}
          title={
            activeImportVariant?.label &&
            activeImportVariant.label !== t("Import")
              ? `${t("Import")} ${activeImportVariant.label}`
              : t("Import")
          }
          size={"xl"}
        >
          {activeImportVariant?.importModes?.length > 0 && (
            <Select
              label="Import Mode"
              data={activeImportVariant.importModes.map((mode) => ({
                value: mode.value,
                label: mode.label,
              }))}
              value={importMode}
              onChange={setImportMode}
              disabled={importing}
              mb="md"
            />
          )}
          <FileInput
            placeholder="Pick file"
            label="Data File"
            withAsterisk
            value={importFile}
            onChange={setImportFile}
            disabled={importing}
          />
          {activeTemplateDownloads
            .filter((item) => item?.url)
            .map((item, index) => (
              <Group mt={index === 0 ? "md" : "xs"} spacing={"xs"} key={item.url}>
                <IconDownload size="1rem" style={{ marginRight: "0.5rem" }} />
                <Anchor
                  size="xs"
                  component="button"
                  type="button"
                  onClick={() => downloadTemplate(item.url, item.text)}
                >
                  {item.text ?? templateDownloadText}
                </Anchor>
              </Group>
            ))}
          <Button onClick={handleImport} mt="xl" size="xs" loading={importing}>
            Import
          </Button>
        </Modal>
        {renderForm()}
      </div>
    </Page>
  );
}

export const AddActionButton = ({
  handleActionClick,
  justify = "right",
  mb = "xl",
  label = "Add",
  mt = "0",
}) => {
  const { t } = useTranslation();
  return (
    <Group justify={justify} mb={mb} mt={mt}>
      <Button size="xs" variant="default" onClick={() => handleActionClick()}>
        <Group>
          <IconCirclePlus size={"1rem"} />
          {t(label)}
        </Group>
      </Button>
      {/* <UnstyledButton onClick={() => handleActionClick()} ml="md">
        <Group gap={"xs"}>
          <ActionIcon>
            <IconCirclePlus size={"1rem"} />
          </ActionIcon>
          <Text size={"xs"}>Add</Text>
        </Group>
      </UnstyledButton> */}
    </Group>
  );
};
